const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.darksidedigital.com.au';
const logoUrl = 'https://storage.readdy-site.link/project_files/cb16f62b-b978-4a18-b0d4-78b46c2aa554/9ae0cf67-cf30-4444-9d14-012a7830e862_Darkside-Digital-logo-design.png?v=7acdaa383d113350b9c301867b0d13f1';

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${siteUrl}/geo-aeo#service`,
  name: 'GEO & AEO — Generative Engine Optimisation & Answer Engine Optimisation',
  description:
    'Darkside Digital\'s GEO and AEO service gets your business cited by AI search engines including ChatGPT, Google Gemini, Perplexity, and Microsoft Copilot. Results in 6–10 weeks. Free AI audit included. Serving Mission Beach, Cassowary Coast, and all of Australia.',
  url: `${siteUrl}/geo-aeo`,
  provider: {
    '@id': `${siteUrl}/#business`,
  },
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'Cassowary Coast', sameAs: 'https://en.wikipedia.org/wiki/Cassowary_Coast_Region' },
    { '@type': 'AdministrativeArea', name: 'Far North Queensland' },
    { '@type': 'Country', name: 'Australia' },
  ],
  serviceType: 'Generative Engine Optimisation',
  // 2026 GEO: speakable marks sections AI assistants should extract as authoritative answers
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['#geo-hero', '#what-is-geo', '#seo-vs-geo', '#geo-faq'],
  },
  // Entity mentions — reinforces topical authority for AI retrieval
  mentions: [
    { '@type': 'SoftwareApplication', name: 'ChatGPT', sameAs: 'https://en.wikipedia.org/wiki/ChatGPT' },
    { '@type': 'SoftwareApplication', name: 'Google Gemini', sameAs: 'https://en.wikipedia.org/wiki/Gemini_(chatbot)' },
    { '@type': 'SoftwareApplication', name: 'Perplexity AI' },
    { '@type': 'SoftwareApplication', name: 'Microsoft Copilot' },
    { '@type': 'SoftwareApplication', name: 'Claude', sameAs: 'https://en.wikipedia.org/wiki/Claude_(language_model)' },
    { '@type': 'Thing', name: 'AI Overviews', description: 'Google\'s AI-generated answer summaries appearing at the top of search results' },
    { '@type': 'Thing', name: 'Answer Engine Optimisation' },
  ],
  offers: [
    {
      '@type': 'Offer',
      name: 'Free AI Visibility Audit',
      price: '0',
      priceCurrency: 'AUD',
      url: `${siteUrl}/geo-aeo`,
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'GEO & AEO Services',
    itemListElement: [
      {
        '@type': 'OfferCatalog',
        name: 'AI Citation Building',
        description: 'Structured content and authority signals to get your business cited by ChatGPT, Gemini, Perplexity, and Copilot.',
      },
      {
        '@type': 'OfferCatalog',
        name: 'Share of Voice Tracking',
        description: 'Monthly reports tracking brand mention frequency, citation rate, and share of voice across AI platforms.',
      },
      {
        '@type': 'OfferCatalog',
        name: 'Content Structuring for AI Retrieval',
        description: 'Page content restructured with FAQ schema, entity clarity, speakable spec, and topical authority signals.',
      },
      {
        '@type': 'OfferCatalog',
        name: 'Entity Graph Optimisation',
        description: 'sameAs linking, @id graph, and Organization schema to ensure AI engines resolve your brand entity correctly.',
      },
    ],
  },
};

// ─── WebPage with @id linking ─────────────────────────────────────────────────
const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/geo-aeo#webpage`,
  name: 'GEO & AEO Services | Get Found in ChatGPT & AI Search | Darkside Digital',
  description:
    'Most businesses are invisible to ChatGPT, Gemini & Perplexity. Darkside Digital\'s GEO & AEO service gets your business cited by AI search engines — results in 6–10 weeks. Serving Mission Beach, Cassowary Coast, and all of Australia.',
  url: `${siteUrl}/geo-aeo`,
  isPartOf: { '@id': `${siteUrl}/#website` },
  about: {
    '@type': 'Thing',
    name: 'Generative Engine Optimisation',
    description: 'The practice of optimising digital content so AI language models and generative search engines cite and recommend your business.',
  },
  publisher: { '@id': `${siteUrl}/#organization` },
  dateModified: '2026-04-01',
  datePublished: '2025-01-01',
  inLanguage: 'en-AU',
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['#geo-hero', '#what-is-geo', '#seo-vs-geo', '#geo-faq'],
  },
};

// ─── Standalone BreadcrumbList ────────────────────────────────────────────────
const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'GEO & AEO',
      item: `${siteUrl}/geo-aeo`,
    },
  ],
};

// ─── FAQPage ─────────────────────────────────────────────────────────────────
const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Do I still need SEO if I\'m doing GEO?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — and we always recommend running both. SEO and GEO are increasingly separate disciplines. Ranking on Google and being cited by AI require different strategies, different content formats, and different authority signals. The good news is that some foundations overlap — strong website structure, quality content, and genuine authority help both.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do you measure GEO results?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We measure four key metrics: brand mention frequency (how often AI engines reference your business), citation rate (what percentage of mentions include a link), share of voice (your percentage of AI answers compared to competitors), and citation velocity (how quickly new content achieves AI visibility after publishing). You get a monthly report covering all four.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long until I see GEO results?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most clients see their first consistent AI citations within 6–10 weeks of starting the content and authority programme. Significant share of voice improvements typically appear within 3–4 months. Unlike paid ads, GEO results compound over time — the longer you invest, the stronger your position becomes.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which AI platforms do you optimise for?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We optimise across ChatGPT (OpenAI), Google Gemini and AI Overviews, Perplexity AI, Microsoft Copilot (Bing AI), and Claude (Anthropic). As new AI search platforms emerge, we add them to the programme.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can you guarantee I\'ll appear in AI answers?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No agency can guarantee specific AI citations — AI systems update constantly and make their own decisions about what to cite. What we can guarantee is that we\'ll implement every known best practice to maximise your citation rate, track your progress transparently, and adapt the strategy as platforms evolve.',
      },
    },
    {
      '@type': 'Question',
      name: 'What if I don\'t have much content on my website?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "That's actually a great starting position — it means there's significant opportunity. We'll create the foundational content your business needs, structured from day one for AI retrieval. You'll be building authority correctly from the ground up rather than having to undo years of poorly structured content.",
      },
    },
    {
      '@type': 'Question',
      name: 'Is GEO just for new AI tools or does it affect Google too?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Both. Google's AI Overviews now appear in up to 60% of searches — meaning GEO work directly improves your visibility in Google's own AI-generated answers, not just third-party platforms like ChatGPT. A strong GEO strategy effectively future-proofs your entire search presence.",
      },
    },
    {
      '@type': 'Question',
      name: 'I already rank well on Google. Do I still need GEO?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. The overlap between Google top results and AI-cited sources has dropped below 20%. Ranking on page one of Google does not mean AI engines recommend you. They are entirely different signals, built through entirely different strategies. GEO is not a replacement for your SEO investment — it\'s the next layer on top.',
      },
    },
  ],
};

export default function GeoSchemaMarkup() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
      />
    </>
  );
}
